import React, { createContext, useContext, useEffect, useReducer, useRef, ReactNode } from 'react';
import { onAuthStateChanged, User as FirebaseUser } from 'firebase/auth';
import { dynamicFirebaseService } from '../services/dynamicFirebaseService';
import { integratedAuthService, hasAppleCompletionFragment } from '../services/integratedAuthService';
import { useClientConfiguration } from './useClientConfiguration';
import { useClientId } from './useClientId';
import { extractReturnUrl, getStoredReturnUrl } from '../utils/urlUtils';
import {
  AuthState,
  AuthContextType,
  FirebaseUserData,
  PlatformAuthResponse,
  AppleSignInFlow
} from '../types/auth';

// Authentication state actions
type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_USER'; payload: FirebaseUserData | null }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_PLATFORM_RESPONSE'; payload: PlatformAuthResponse | null }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SIGN_OUT' };

// Extended authentication state with platform response
interface ExtendedAuthState extends AuthState {
  platformResponse: PlatformAuthResponse | null;
}

// Initial authentication state
const initialAuthState: ExtendedAuthState = {
  user: null,
  loading: true,
  error: null,
  isAuthenticated: false,
  platformResponse: null
};

// Authentication state reducer
const authReducer = (state: ExtendedAuthState, action: AuthAction): ExtendedAuthState => {
  switch (action.type) {
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload
      };
    case 'SET_USER':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: action.payload !== null,
        loading: false,
        error: null
      };
    case 'SET_PLATFORM_RESPONSE':
      return {
        ...state,
        platformResponse: action.payload
      };
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        loading: false
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null
      };
    case 'SIGN_OUT':
      return {
        ...initialAuthState,
        loading: false
      };
    default:
      return state;
  }
};

// Create authentication context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Authentication provider props
interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Authentication Provider Component
 * Manages authentication state and provides auth methods to child components
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, dispatch] = useReducer(authReducer, initialAuthState);
  // The Apple completion fragment is single-use; this keeps the effect from retrying it
  const appleWebFlowAttemptedRef = useRef(false);
  // getRedirectResult() is likewise single-use - Firebase clears the pending credential once read
  const redirectResultAttemptedRef = useRef(false);
  // Keeps the safety-net exchange to one attempt per page load, so it cannot fire repeatedly
  // alongside the inline exchange that signIn / signInWithGoogle / signInWithApple already perform
  const automaticExchangeAttemptedRef = useRef(false);
  const { clientId } = useClientId();
  const { configuration, isLoading: configLoading, error: configError } = useClientConfiguration(clientId);

  // Set up Firebase auth state listener after Firebase is initialized
  useEffect(() => {
    if (!configuration || configLoading || configError) {
      return;
    }

    let unsubscribe: (() => void) | undefined;

    try {
      const auth = dynamicFirebaseService.getAuth();
      unsubscribe = onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
      if (firebaseUser) {
        // Convert Firebase user to our user data format with provider information
        const providerId = firebaseUser.providerData.length > 0 
          ? firebaseUser.providerData[0].providerId 
          : 'firebase';

        const userData: FirebaseUserData = {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          emailVerified: firebaseUser.emailVerified,
          displayName: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL,
          providerId,
          createdAt: firebaseUser.metadata.creationTime || new Date().toISOString(),
          lastSignInAt: firebaseUser.metadata.lastSignInTime || new Date().toISOString()
        };
        
        console.log('Firebase auth state changed - user authenticated', {
          uid: userData.uid,
          email: userData.email,
          providerId: userData.providerId,
          isGoogleUser: providerId === 'google.com'
        });
        
        dispatch({ type: 'SET_USER', payload: userData });

        // Safety net for any provider that reaches an authenticated state without a platform
        // response - most importantly Apple returning from signInWithRedirect, whose result the
        // effect below cannot always consume (Safari partitions the auth-domain storage the
        // pending credential lives in). This was gated on google.com, which left those users
        // signed in to Firebase but tokenless: AuthenticationPage then waits for a platform
        // response that never arrives and the login page simply sits there.
        if (!authState.platformResponse && !automaticExchangeAttemptedRef.current) {
          console.log('Authenticated without a platform response, attempting automatic token exchange', {
            providerId
          });
          automaticExchangeAttemptedRef.current = true;
          await handleAutomaticTokenExchange();
        }
      } else {
        console.log('Firebase auth state changed - user signed out');
        dispatch({ type: 'SET_USER', payload: null });
      }
    });

    } catch (error) {
      console.error('Error setting up Firebase auth state listener:', error);
      dispatch({ type: 'SET_ERROR', payload: 'errors.auth.initFailed' });
    }

    // Cleanup subscription on unmount
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [configuration, configLoading, configError, authState.platformResponse]);

  // `loading` starts true because we do not yet know whether a session exists. Normally
  // onAuthStateChanged answers that as soon as Firebase is ready. When the client configuration
  // never resolves - no client_id in the URL, or the config request failed - Firebase is never
  // initialized, that listener never attaches, and nothing else would ever clear the flag:
  // AuthenticationPage would render its spinner forever and hide the underlying error.
  //
  // This used to be masked by the redirect-result effect below, which dispatched SET_LOADING(false)
  // from its `finally` on every mount regardless of outcome. That was incidental, not intentional,
  // and it disappeared once the effect was correctly gated on clientId - so state the rule here.
  useEffect(() => {
    // Only once the attempt has genuinely settled: the config request failed, or there is no
    // client_id to load one for. Clearing merely because `configuration` is still null would fire
    // on the first render, before the request has even started, and unmask the login form while it
    // still believes the configuration is missing.
    if (configError || (!clientId && !configLoading)) {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [clientId, configuration, configLoading, configError]);

  // Consume the pending signInWithRedirect result, for Apple as much as for Google - the service
  // layer picks the provider off the credential itself.
  //
  // Gated on clientId and configuration rather than running bare on mount. clientId arrives from
  // useClientId's own effect, so it is still null on the first render: the previous mount-only
  // version hit the `if (!clientId) return` guard inside handleGoogleRedirectResult and, with an
  // empty dependency array, never ran again. The pending credential was dropped, and the user was
  // left signed in to Firebase with no platform token and no redirect. configuration is required
  // for the same reason the Apple web-flow effect below requires it: the exchange goes through the
  // Firebase SDK, which useClientConfiguration initializes asynchronously.
  useEffect(() => {
    if (!clientId || !configuration || configLoading || configError) {
      return;
    }

    // Firebase clears the pending credential on the first read, so never attempt it twice -
    // `configuration` is a fresh object on some renders and would otherwise re-run this
    if (redirectResultAttemptedRef.current) {
      return;
    }

    // A page load carrying an Apple completion fragment is finishing the server-side flow, which
    // redeems its own custom token. Reading a pending redirect result here would start a second,
    // unrelated Firebase auth operation against the same Auth instance at exactly that moment -
    // and the SDK's redirect resolver tidies the URL as it goes. There is never a pending redirect
    // to collect on this page anyway, so skip it and let the Apple effect below own the sign-in.
    if (hasAppleCompletionFragment()) {
      return;
    }

    redirectResultAttemptedRef.current = true;

    handleGoogleRedirectResult().catch(error => {
      console.error('Error checking OAuth redirect result:', error);
    });
  }, [clientId, configuration, configLoading, configError]);

  // Finish the server-side Apple flow when Apple has redirected back here.
  //
  // Waits on `configuration` rather than just clientId: the custom token is redeemed through the
  // Firebase SDK, and Firebase is initialized asynchronously by useClientConfiguration. Keying
  // this on clientId alone fired it before initializeWithClientId had run, so signInWithCustomToken
  // threw "Firebase Auth not initialized" and the token was consumed and lost. The same guard the
  // auth-state listener above uses is the signal that Firebase is actually ready.
  useEffect(() => {
    if (!clientId || !configuration || configLoading || configError) {
      return;
    }

    // The fragment is single-use, so never attempt it twice - `configuration` is a fresh object on
    // some renders, which would otherwise re-run this after the token had already been cleared
    if (appleWebFlowAttemptedRef.current) {
      return;
    }

    appleWebFlowAttemptedRef.current = true;

    const completeAppleWebFlow = async () => {
      dispatch({ type: 'SET_LOADING', payload: true });

      try {
        const result = await integratedAuthService.completeAppleWebFlow(clientId);

        if (result === null) {
          // Not an Apple completion, nothing to do
          return;
        }

        if (!result.success) {
          if (result.error) {
            console.error('Apple web flow completion failed:', result.error);
            dispatch({ type: 'SET_ERROR', payload: result.error });
          } else {
            console.log('Apple sign-in cancelled by user');
          }
        } else if (result.platformResponse) {
          console.log('Apple web flow completed, platform response received');
          dispatch({ type: 'SET_PLATFORM_RESPONSE', payload: result.platformResponse });
        }
      } catch (error) {
        console.error('Unexpected error completing Apple web flow:', error);
        dispatch({ type: 'SET_ERROR', payload: 'errors.integrated.authFailed' });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    completeAppleWebFlow();
  }, [clientId, configuration, configLoading, configError]);

  /**
   * Sign in with email and password and exchange for platform JWT
   */
  const signIn = async (email: string, password: string, returnUrl?: string): Promise<void> => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'CLEAR_ERROR' });

    try {
      if (!clientId) {
        dispatch({ type: 'SET_ERROR', payload: 'errors.auth.clientIdRequired' });
        return;
      }

      const result = await integratedAuthService.signIn(email, password, clientId, returnUrl);
      
      if (!result.success) {
        dispatch({ type: 'SET_ERROR', payload: result.error || 'errors.auth.signInFailed' });
      } else if (result.platformResponse) {
        dispatch({ type: 'SET_PLATFORM_RESPONSE', payload: result.platformResponse });
      }
      // User state will be updated by onAuthStateChanged listener
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'errors.auth.unexpected' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  /**
   * Sign in with Google and exchange for platform JWT
   */
  const signInWithGoogle = async (returnUrl?: string): Promise<void> => {
    console.log('Google sign-in initiated from useAuth hook', { returnUrl });
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'CLEAR_ERROR' });

    try {
      if (!clientId) {
        dispatch({ type: 'SET_ERROR', payload: 'errors.auth.clientIdRequired' });
        return;
      }

      const result = await integratedAuthService.signInWithGoogle(clientId, returnUrl);
      
      if (!result.success) {
        // Only show error if it's not a user cancellation
        if (result.error) {
          console.error('Google sign-in failed:', result.error);
          dispatch({ type: 'SET_ERROR', payload: result.error });
        } else {
          console.log('Google sign-in cancelled by user');
        }
      } else if (result.platformResponse) {
        console.log('Google sign-in successful, platform response received');
        dispatch({ type: 'SET_PLATFORM_RESPONSE', payload: result.platformResponse });
      } else {
        console.log('Google sign-in initiated redirect flow');
      }
      // User state will be updated by onAuthStateChanged listener
    } catch (error) {
      console.error('Unexpected error during Google sign-in:', error);
      dispatch({ type: 'SET_ERROR', payload: 'errors.auth.googleUnexpected' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  /**
   * Sign in with Apple and exchange for platform JWT
   */
  const signInWithApple = async (
    returnUrl?: string,
    flow: AppleSignInFlow = 'firebase',
    flowKey?: string
  ): Promise<void> => {
    console.log('Apple sign-in initiated from useAuth hook', { returnUrl, flow, flowKey });
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'CLEAR_ERROR' });

    try {
      if (!clientId) {
        dispatch({ type: 'SET_ERROR', payload: 'errors.auth.clientIdRequired' });
        return;
      }

      const result = await integratedAuthService.signInWithApple(clientId, returnUrl, flow, flowKey);
      
      if (!result.success) {
        // Only show error if it's not a user cancellation
        if (result.error && !result.error.includes('cancelled')) {
          console.error('Apple sign-in failed:', result.error);
          dispatch({ type: 'SET_ERROR', payload: result.error });
        } else {
          console.log('Apple sign-in cancelled by user');
        }
      } else if (result.platformResponse) {
        console.log('Apple sign-in successful, platform response received');
        dispatch({ type: 'SET_PLATFORM_RESPONSE', payload: result.platformResponse });
      }
    } catch (error) {
      console.error('Unexpected error during Apple sign-in:', error);
      dispatch({ type: 'SET_ERROR', payload: 'errors.auth.appleUnexpected' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  /**
   * Check if Apple Sign-In is available
   */
  const isAppleSignInAvailable = (): boolean => {
    return integratedAuthService.isAppleSignInAvailable();
  };

  /**
   * Handle Google redirect result after redirect flow
   */
  const handleGoogleRedirectResult = async (): Promise<void> => {
    console.log('Handling Google redirect result from useAuth hook');
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'CLEAR_ERROR' });

    try {
      if (!clientId) {
        console.log('No client ID available for Google redirect result handling');
        return;
      }

      const result = await integratedAuthService.handleGoogleRedirectResult(clientId);
      
      if (result && !result.success && result.error) {
        console.error('Google redirect result failed:', result.error);
        dispatch({ type: 'SET_ERROR', payload: result.error });
      } else if (result && result.platformResponse) {
        console.log('Google redirect result successful, platform response received');
        dispatch({ type: 'SET_PLATFORM_RESPONSE', payload: result.platformResponse });
      } else if (result === null) {
        console.log('No Google redirect result found (normal case)');
      }
      // User state will be updated by onAuthStateChanged listener
    } catch (error) {
      console.error('Unexpected error processing Google redirect result:', error);
      dispatch({ type: 'SET_ERROR', payload: 'errors.auth.googleRedirectUnexpected' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  /**
   * Sign out current user from both platform and Firebase
   */
  const signOut = async (): Promise<void> => {
    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      if (!clientId) {
        dispatch({ type: 'SET_ERROR', payload: 'errors.auth.clientIdRequiredSignOut' });
        return;
      }

      const result = await integratedAuthService.signOut(clientId);
      
      if (result.success) {
        dispatch({ type: 'SIGN_OUT' });
      } else {
        dispatch({ type: 'SET_ERROR', payload: result.error || 'errors.auth.signOutFailed' });
      }
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'errors.auth.signOutUnexpected' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  /**
   * Send password reset email
   */
  const resetPassword = async (email: string): Promise<void> => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'CLEAR_ERROR' });

    try {
      const result = await integratedAuthService.resetPassword(email);
      
      if (!result.success) {
        dispatch({ type: 'SET_ERROR', payload: result.error || 'errors.auth.passwordResetFailed' });
      }
      // Success message could be handled by the component calling this method
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'errors.auth.unexpected' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  /**
   * Clear authentication error
   */
  const clearError = (): void => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  /**
   * Handle automatic token exchange for returning users
   */
  const handleAutomaticTokenExchange = async (): Promise<void> => {
    try {
      console.log('Starting automatic token exchange for returning user');
      
      const currentUser = integratedAuthService.getCurrentUser();
      if (!currentUser) {
        console.log('No current user found for automatic token exchange');
        return;
      }

      if (!clientId) {
        console.log('No client ID available for automatic token exchange');
        return;
      }

      // Prefer the live query string over session storage. Both normally agree - useReturnUrl
      // mirrors the parameter into storage on mount - but the URL is the authoritative copy, and
      // storage can be empty in a browser that refuses it.
      const returnUrl = extractReturnUrl() ?? getStoredReturnUrl();

      const platformResponse = await integratedAuthService.exchangeTokenForPlatformJWT(clientId, returnUrl || undefined);
      
      if (platformResponse) {
        console.log('Automatic token exchange successful', {
          hasToken: !!platformResponse.platformToken,
          redirectUrl: platformResponse.redirectUrl
        });
        dispatch({ type: 'SET_PLATFORM_RESPONSE', payload: platformResponse });
      } else {
        console.log('Automatic token exchange failed - no platform response');
      }
    } catch (error) {
      console.error('Automatic token exchange failed:', error);
      // Don't dispatch error for automatic attempts - fail silently
    }
  };

  /**
   * Exchange current Firebase token for platform JWT
   */
  const exchangeTokenForPlatformJWT = async (returnUrl?: string): Promise<PlatformAuthResponse | null> => {
    try {
      if (!clientId) {
        console.error('Client ID is required for token exchange');
        return null;
      }

      const platformResponse = await integratedAuthService.exchangeTokenForPlatformJWT(clientId, returnUrl);
      if (platformResponse) {
        dispatch({ type: 'SET_PLATFORM_RESPONSE', payload: platformResponse });
      }
      return platformResponse;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'errors.auth.tokenExchangeFailed' });
      return null;
    }
  };

  /**
   * Validate return URL with backend
   */
  const validateReturnUrl = async (returnUrl: string): Promise<{ isValid: boolean; error?: string }> => {
    if (!clientId) {
      return {
        isValid: false,
        error: 'errors.auth.clientIdRequiredReturnUrl'
      };
    }
    return integratedAuthService.validateReturnUrl(returnUrl, clientId);
  };

  /**
   * Check if user has valid authentication session
   */
  const hasValidSession = async (): Promise<boolean> => {
    return integratedAuthService.hasValidSession();
  };

  // Context value
  const contextValue: AuthContextType = {
    authState: {
      ...authState,
      platformResponse: authState.platformResponse
    },
    signIn,
    signInWithGoogle,
    signInWithApple,
    signOut,
    resetPassword,
    clearError,
    exchangeTokenForPlatformJWT,
    validateReturnUrl,
    handleGoogleRedirectResult,
    hasValidSession,
    isAppleSignInAvailable
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Custom hook to use authentication context
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};